import uuid from 'uuid/v4';

import type { DocNode } from '@atlaskit/adf-schema';
import {
	type JSONDocNode,
	type JSONNode,
	JSONTransformer,
} from '@atlaskit/editor-json-transformer';
import { type EditorState } from '@atlaskit/editor-prosemirror/state';
import { getMentionMap } from '@atlassian/ai-model-io/utils/mention-map';
import { getXProductHeader } from '@atlassian/editor-ai-common/api/request';

import type { GetMentionNameDetails } from '../../../../types/types';

import type {
	ErrorResponse,
	Recommendation,
	RequestGenerator,
	TriggerContext,
	ValidRequest,
	ValidResponse,
} from './types';

// Ignored via go/ees005
// eslint-disable-next-line require-unicode-regexp
const sanitizeUrl = (url: string) => url.replace(/[^\w\-/.:%?=]+/g, '');

export async function getFullPageContent({
	editorState,
	getMentionNameDetails,
}: {
	editorState: EditorState;
	getMentionNameDetails?: GetMentionNameDetails;
}) {
	const { doc, schema } = editorState;
	let mentionMap;
	if (getMentionNameDetails) {
		mentionMap = await getMentionMap({ node: doc, getMentionNameDetails });
	}

	const serializer = new JSONTransformer(schema, mentionMap);

	return { version: 1, ...serializer.encodeNode(doc) };
}

export async function* createRequestGenerator({
	endpoint,
	product,
	context,
}: {
	endpoint: string;
	product: string;
	context: TriggerContext;
}): RequestGenerator {
	const { view: editorView, locale, getMentionNameDetails } = context;

	// Ignored via go/ees007
	// eslint-disable-next-line @atlaskit/editor/enforce-todo-comment-format
	// FIXME: Rather then removing all the work we added in S+G around queing limits and purging requests. I'm going to
	// set the limits to max value, this way the limits are not enforced and the BE just gets all changes always. This
	// way in future if they want to have limits we can just reset the limit settings.
	/**
	 * QUEUE CONSTRAINTS:
	 * Input size sum (all parts): 10,000 chars
	 * Input size per part: 5,000 chars
	 * Max parts: 50
	 */

	const abortController = new AbortController();

	try {
		const docContent = await getFullPageContent({
			editorState: editorView.state,
			getMentionNameDetails,
		});
		const payload: ValidRequest = {
			ai_feature_input: {
				// Only add uncached paragraphs to requests, since cached ones have already been yielded
				triggers: [
					{
						trigger_id: uuid(),
						content_url: sanitizeUrl(document.location.href),
						trigger_type: 'PARAGRAPH_COMPLETE',
						paragraph_content: docContent,
					},
				],
			},
		};

		// track durations
		const startTime = performance.now();

		const response = await fetch(endpoint, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json;charset=UTF-8',
				'X-Experience-Id': 'proactive-paragraph-complete',
				'Accept-Language': locale,
				...getXProductHeader(product),
			},
			body: JSON.stringify(payload),
			credentials: 'include',
			signal: abortController.signal,
			mode: 'cors',
		});

		yield {
			state: 'trackedDuration',
			duration: performance.now() - startTime,
		};

		const result = await parseResponse(response);

		if (result.state === 'failed') {
			yield result;
			yield { state: 'done' };
			return;
		}

		const { triggers } = result.body.ai_feature_output;

		// Ignored via go/ees007
		// eslint-disable-next-line @atlaskit/editor/enforce-todo-comment-format
		// FIXME: This is not ideal, it checks for any errors in the response and then bails.
		// it would be nice to handle suggestions which are also included with the errors.
		const errors = triggers.reduce<string[]>((acc, trigger) => {
			if ('errors' in trigger) {
				acc.push(trigger.errors.error_type);
			}
			return acc;
		}, []);

		const serializer = new JSONTransformer(editorView.state.schema);

		const recommendations = triggers.reduce<Recommendation[]>((acc, trigger) => {
			if ('recommendations' in trigger) {
				return acc.concat(
					trigger.recommendations.map<Recommendation>((recommendation) => {
						// XXX: We can only parse doc nodes, just in case the backend responds with a raw adf node which isn't encapsulated
						// with a doc node then we will attempt correct this error before we parse it.
						function getTransformContent(replacement_adf: JSONNode) {
							const replacement: JSONDocNode =
								replacement_adf.type !== 'doc'
									? {
											version: 1,
											type: 'doc',
											content: [replacement_adf],
										}
									: (replacement_adf as JSONDocNode);
							return {
								// Warning -- document is asserted due to a mismatch in the editor-json-transformer and renderer types.
								// JSONDocNode _should_ be runtime compatible with DocNode -- but does not have compatible types.
								// See the following files for more details
								// - packages/editor/editor-json-transformer/src/types.ts
								// - packages/editor/adf-schema/src/schema/nodes/doc.ts
								document: replacement as DocNode,
								getContent: () => serializer.parse(replacement).content,
							};
						}

						return {
							chunkId: trigger.trigger_id,
							rid: recommendation.recommendation_id,
							// XXX: We will not assume that the recommendation id is globally unique. However it should at minimum
							// be unique to other local recommendations. We can make this id globally unique by prepending the
							// trigger id which is gloabally unique.
							id: `${trigger.trigger_id}_${recommendation.recommendation_id}`,
							transformContent: recommendation.transformation_details
								? getTransformContent(recommendation.transformation_details.replacement_adf)
								: undefined,
							transformType: recommendation.transformation_type,
							transformAction: recommendation.recommendation_name,
							details: recommendation.recommendation_details,
							type: recommendation.recommendation_type,
							score: recommendation.recommendation_score ?? 0,
							isViewed: false,
							filtered: false,
						};
					}),
				);
			}
			return acc;
		}, []);

		yield { state: 'parsed', recommendations };

		if (errors.length) {
			yield {
				state: 'failed',
				reason: 'backend',
				errors,
				statusCode: response.status,
			};
		}
		// Ignored via go/ees005
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (error.name === 'AbortError') {
			yield {
				state: 'failed',
				reason: 'aborted',
				errors: ['Streaming aborted'],
				statusCode: error.status,
			};
		} else {
			yield {
				state: 'failed',
				reason: 'unhandled',
				errors:
					error instanceof Error &&
					['RangeError', 'TypeError', 'TransformError'].includes(error.name)
						? [error.message]
						: ['unhandled'],
				statusCode: error.status,
			};
		}
	}

	yield { state: 'done' };
	return;
}

function isValidResponse(response: unknown): response is ValidResponse {
	return typeof response === 'object' && response !== null && 'ai_feature_output' in response;
}

function isErrorResponse(response: unknown): response is ErrorResponse {
	return (
		typeof response === 'object' &&
		response !== null &&
		'errorMessage' in response &&
		'errors' in response &&
		'statusCode' in response
	);
}

async function parseResponse(response: Response): Promise<
	| {
			state: 'success';
			body: ValidResponse;
	  }
	| {
			state: 'failed';
			reason: 'network' | 'backend' | 'parsing' | 'rate-limited';
			errors: string[];
			statusCode: number;
			retryAfter?: number;
	  }
> {
	if (response.status === 429) {
		// The BE are unable to add the header due to constraints with the service proxy
		// There is a ticket to add this via feature request
		// https://hello.jira.atlassian.cloud/browse/SPSVC-28
		// For now we are just hard coding the value

		const retryAfter = parseInt(response.headers.get('Retry-After') ?? '');
		return {
			state: 'failed',
			reason: 'rate-limited',
			// Retry-After is formatted in seconds, normalizing to milliseconds
			retryAfter: !isNaN(retryAfter) ? retryAfter * 1000 : undefined,
			statusCode: response.status,
			errors: ['tooManyRequests'],
		};
	}

	try {
		const body = await response.json();

		if (isValidResponse(body)) {
			return {
				state: 'success',
				body,
			};
		}

		if (isErrorResponse(body)) {
			return {
				state: 'failed',
				reason: 'backend',
				errors: [body.errorMessage, ...body.errors],
				statusCode: body.statusCode,
			};
		}
	} catch (parsingError) {
		return {
			state: 'failed',
			reason: 'parsing',
			errors: ['parsingError'],
			statusCode: response.status,
		};
	}

	if (!response.body) {
		return {
			state: 'failed',
			reason: 'network',
			errors: ['response.body missing'],
			statusCode: response.status,
		};
	}

	return {
		state: 'failed',
		reason: 'backend',
		errors: ['Invalid response'],
		statusCode: response.status,
	};
}
